import { Injectable } from '@angular/core';
import { UserProfile } from 'src/app/auth/profile/profile.service';
import { SettingsService } from 'src/app/core/settings/settings.service';
import { TierService } from 'src/app/shared/services/tier.service';
import { OrganizationService } from 'src/app/chatstyle/organization/organization.service';
import { TenantImpersonationService } from 'src/app/auth/tenant-impersonation/tenant-impersonation.service';
import { Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';

import { menu } from 'src/app/auth/menu';
import { MenuItem } from 'src/app/auth/menu-items';
import { Organization } from 'src/app/shared/models/core.model';

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  menuItems: MenuItem[] = [];

  profile: UserProfile;
  private currentTier: string | null = null;

  constructor(
    private settings: SettingsService,
    private tierService: TierService,
    private orgService: OrganizationService,
    private tenantImpersonationService: TenantImpersonationService
  ) {}

  private loadCurrentTier(): Observable<void | Organization> {
    return this.orgService.getMyOrganization().pipe(
      tap(org => {
        this.currentTier = org?.tier || null;
      }),
      catchError(error => {
        console.error('Error loading organization tier:', error);
        this.currentTier = null;
        return of(null);
      }),
      tap(() => {})
    );
  }

  load(profile: UserProfile): Observable<void | Organization> {
    this.profile = profile;

    // Ensure tier is loaded before building menu
    return this.loadCurrentTier().pipe(
      tap(() => {
        this.menuItems = [];

        this.addMenu(menu);
      })
    );
  }

  /**
   * Refresh tier and reload menus (call after organization change)
   */
  refreshTier(): Observable<void | Organization> {
    if (this.profile) {
      return this.load(this.profile);
    }
    return of(undefined);
  }

  private addMenuItems(items: MenuItem[], targetArray: MenuItem[]) {
    targetArray.push(...this.filterMenuItems(items));
  }

  /**
   * Privilege + tier filtering, applied recursively so group children are gated on their
   * own privilege / tierRestricted rather than inheriting the group parent's. A group whose
   * children all filter out is dropped entirely - we never render an empty group.
   *
   * Group parents carry no link, so a parent that survives with no children would be an
   * inert row.
   */
  private filterMenuItems(items: MenuItem[]): MenuItem[] {
    const result: MenuItem[] = [];

    items.forEach((item) => {
      if (
        !this.hasPrivilegeAccess(item) ||
        !this.hasTierAccess(item) ||
        this.isHiddenWhileImpersonating(item)
      ) {
        return;
      }

      if (item.submenu) {
        const submenu = this.filterMenuItems(item.submenu);
        if (submenu.length === 0) {
          return;
        }
        result.push({ ...item, submenu });
        return;
      }

      result.push(item);
    });

    return result;
  }

  /**
   * CHAT-959: rows flagged `hideWhileImpersonating` leave the nav for the duration of
   * an impersonation session, regardless of privilege.
   *
   * Operand order is load-bearing, not incidental: `isImpersonating()` reads the OIDC
   * claims on every call, so the cheap flag test has to short-circuit it. Keep the
   * flag on the left if this grows more conditions.
   */
  private isHiddenWhileImpersonating(item: MenuItem): boolean {
    return (
      !!item.hideWhileImpersonating &&
      this.tenantImpersonationService.isImpersonating()
    );
  }

  private hasTierAccess(item: MenuItem): boolean {
    // SuperAdmins have access to all pages regardless of tier
    if (this.hasPrivilege('SuperAdmin')) {
      return true;
    }

    // If item is not tier restricted, allow access
    if (!item.tierRestricted) {
      return true;
    }

    // Check if the menu item is visible for the current tier using the menu item text directly
    return this.tierService.isMenuItemVisible(this.currentTier, item.text);
  }

  addMenu(items: MenuItem[]) {
    this.addMenuItems(items, this.menuItems);
  }

  getMenu() {
    return this.processMenuItems(this.menuItems);
  }

  hasPrivilege(priv: string): boolean {
    if (this.profile) {
      if (
        this.profile.privileges !== null &&
        this.profile.privileges.indexOf(priv) >= 0
      ) {
        return true;
      }
    }
    return false;
  }

  private hasPrivilegeAccess(item: MenuItem): boolean {
    if (!item.privilege) {
      return true;
    }

    let priv = item.privilege;
    let negate = false;

    if (item.privilege.startsWith('!')) {
      priv = item.privilege.substr(1);
      negate = true;
    }

    let hasPriv = false;
    if (this.profile && this.profile.privileges) {
      hasPriv = this.profile.privileges.indexOf(priv) > -1;
    }

    return negate ? !hasPriv : hasPriv;
  }

  private processMenuItems(
    items: Array<any>,
    state?: string,
    configurationType?: string
  ): Array<any> {
    const clonedArray = [];
    const values = this.settings;
    const isSuperAdmin = this.hasPrivilege('SuperAdmin');

    items.forEach((value) => {
      const clone = Object.assign({}, value);

      if (value.link && configurationType) {
        clone.link = value.link.replace(':type', configurationType);
      }

      if (value.elink) {
        clone.elink = value.elink.replace(
          ':help_uri',
          values.getSetting('help_uri')
        );
      }

      if (value.submenu) {
        // Clone children too, so the rendered menu never shares an array with the source
        // MenuItems definitions, and drop a group left empty by state filtering.
        clone.submenu = this.processMenuItems(
          value.submenu,
          state,
          configurationType
        );
        if (clone.submenu.length === 0) {
          return;
        }
      }

      // SuperAdmin bypass: Show all menu items regardless of state
      if (isSuperAdmin) {
        clonedArray.push(clone);
      } else {
        // Non-SuperAdmin: Apply state filtering
        if (!state || !clone.state || clone.state === state) {
          clonedArray.push(clone);
        }
      }
    });

    return clonedArray;
  }
}
